import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Accordion, AccordionItem, AccordionButton, AccordionPanel} from '@chakra-ui/react'

import {FormattedMessage} from 'react-intl'
import {MinusIcon, PlusIcon} from '../../components/custom-icons'

/**
 * A functional component that renders a map accordion for the checkout process on mobile devices.
 * The accordion allows users to toggle between showing and hiding a map, with corresponding messages and icons.
 *
 * @param {Object} props - The properties object.
 * @param {React.ReactNode} props.children - The child components to be rendered inside the accordion panel.
 * @param {boolean} props.isCheckout - A flag indicating whether the component is being used in the checkout process.
 * @param {boolean} props.isMobile - A flag indicating whether the component is being viewed on a mobile device.
 * @returns {React.ReactNode} The rendered accordion component if in checkout and on mobile, otherwise the children components.
 */
const MapAccordion = ({children, isCheckout, isMobile}) => {
    const showMapMessage = (
        <FormattedMessage defaultMessage="View map" id="checkout_click_and_collect.show_map" />
    )
    const hideMapMessage = (
        <FormattedMessage defaultMessage="Hide map" id="checkout_click_and_collect.hide_map" />
    )

    const accordionPlusIcon = <PlusIcon boxSize="12px" />
    const accordionMinusIcon = <MinusIcon boxSize="12px" />

    const [activeToggleMessage, setActiveToggleMessage] = useState(showMapMessage)
    const [activeIcon, setActiveIcon] = useState(accordionPlusIcon)

    const ToggleAccordionText = (accordionOpenedIndex) => {
        if (accordionOpenedIndex === 0) {
            setActiveToggleMessage(hideMapMessage)
            setActiveIcon(accordionMinusIcon)
        } else {
            setActiveToggleMessage(showMapMessage)
            setActiveIcon(accordionPlusIcon)
        }
    }

    return isCheckout && isMobile ? (
        <Accordion allowToggle onChange={ToggleAccordionText}>
            <AccordionItem border="none">
                <AccordionButton width="fit-content">
                    <Box flex="1" textAlign="left" pr="5px" textTransform="uppercase">
                        {activeToggleMessage}
                    </Box>
                    {activeIcon}
                </AccordionButton>
                <AccordionPanel pb={4} style={{height: '550px'}}>
                    {children}
                </AccordionPanel>
            </AccordionItem>
        </Accordion>
    ) : (
        children
    )
}

MapAccordion.propTypes = {
    children: PropTypes.node.isRequired,
    isCheckout: PropTypes.bool.isRequired,
    isMobile: PropTypes.bool.isRequired
}

export default MapAccordion
